/**
 * Re-cases all-caps note bodies into normal sentence form.
 *
 * Triggers only when the text is >= 85% uppercase AND has >= 4 words, so short
 * acronym-only notes (e.g. "AOG MEL") are left untouched. When it runs, each
 * word is lowercased with the first word of every sentence capitalized, while
 * these are preserved:
 * - Tokens containing digits (tails, part numbers, Zulu times, damage codes).
 * - Known acronyms, station codes, carrier codes, IROP codes, and ADS
 *   abbreviations (the latter stay uppercase so the abbreviation expander can
 *   still expand them afterwards).
 * - The Zulu convention: an uppercase Z immediately after a digit becomes
 *   lowercase (2300Z/11 -> 2300z/11).
 * A trailing sentence terminator is added when the note does not already end
 * with one.
 */
import { getMainEntries, getStandaloneEntries, getStoreVersion } from '../data/abbreviationStore';
import { KNOWN_ACRONYMS } from '../data/knownAcronyms';
import { STATIONS, CARRIERS, IROP_FLAGS } from '../constants';

const UPPERCASE_TRIGGER_RATIO = 0.85;
const MIN_WORD_COUNT = 4;

const WORD_OR_GAP = /[A-Za-z0-9/]+|[^A-Za-z0-9/]+/g;
const HAS_DIGIT = /\d/;
const IS_WORD = /^[A-Za-z0-9/]+$/;
const SENTENCE_END = /[.!?]/;

/** Static tokens always preserved in uppercase (compared case-insensitively). */
const BASE_PRESERVED_TOKENS: readonly string[] = [
  ...KNOWN_ACRONYMS,
  ...STATIONS,
  ...Object.values(CARRIERS),
  ...Object.keys(IROP_FLAGS),
];

let cachedVersion = -1;
let preservedTokens: ReadonlySet<string> = new Set<string>(BASE_PRESERVED_TOKENS);

/**
 * Tokens preserved in uppercase: the static set plus the current abbreviation
 * list from the runtime store (so freshly-added abbreviations stay uppercase and
 * remain expandable). Rebuilt only when the store version changes.
 */
function getPreservedTokens(): ReadonlySet<string> {
  const currentVersion = getStoreVersion();
  if (currentVersion !== cachedVersion) {
    preservedTokens = new Set<string>([
      ...BASE_PRESERVED_TOKENS,
      ...getMainEntries().map((entry) => entry.abbreviation.toUpperCase()),
      ...getStandaloneEntries().map((entry) => entry.abbreviation.toUpperCase()),
    ]);
    cachedVersion = currentVersion;
  }
  return preservedTokens;
}

/** Fraction of alphabetic characters that are uppercase (0 when none). */
function uppercaseRatio(text: string): number {
  const letters = text.replace(/[^A-Za-z]/g, '');
  if (letters.length === 0) return 0;
  return letters.replace(/[^A-Z]/g, '').length / letters.length;
}

/** Number of whitespace-separated words. */
function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/** True when the text reads as an all-caps note that should be re-cased. */
export function shouldRecaseNote(text: string): boolean {
  return uppercaseRatio(text) >= UPPERCASE_TRIGGER_RATIO && wordCount(text) >= MIN_WORD_COUNT;
}

/** Lowercases an uppercase Z that immediately follows a digit (Zulu convention). */
function applyZuluConvention(token: string): string {
  return token.replace(/(\d)Z/g, '$1z');
}

/** Re-cases a single word token. */
function recaseWord(
  token: string,
  atSentenceStart: boolean,
  preserved: ReadonlySet<string>,
): string {
  if (HAS_DIGIT.test(token)) return applyZuluConvention(token);
  if (preserved.has(token.toUpperCase())) return token.toUpperCase();
  const lower = token.toLowerCase();
  return atSentenceStart ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
}

/** Appends a period when the (trimmed) text does not end with .!? */
function ensureTrailingPunctuation(text: string): string {
  const body = text.replace(/\s+$/, '');
  if (!body || SENTENCE_END.test(body.slice(-1))) return text;
  return `${body}.${text.slice(body.length)}`;
}

/**
 * Returns the note text re-cased to sentence form when it reads as an all-caps
 * note; otherwise returns it unchanged.
 */
export function normalizeNoteCasing(text: string): string {
  if (!text || !shouldRecaseNote(text)) return text;
  const preserved = getPreservedTokens();
  let atSentenceStart = true;
  let result = '';
  for (const chunk of text.match(WORD_OR_GAP) ?? []) {
    if (IS_WORD.test(chunk)) {
      result += recaseWord(chunk, atSentenceStart, preserved);
      atSentenceStart = false;
    } else {
      result += chunk;
      if (SENTENCE_END.test(chunk)) atSentenceStart = true;
    }
  }
  return ensureTrailingPunctuation(result);
}

/**
 * Capitalizes the first letter of the first word of each sentence, leaving all
 * other characters untouched. Intended as a final pass after abbreviation
 * expansion (which can lowercase a sentence-leading abbreviation). Digit-leading
 * and already-uppercase tokens are unaffected.
 */
export function capitalizeSentenceStarts(text: string): string {
  if (!text) return text;
  let atSentenceStart = true;
  let result = '';
  for (const chunk of text.match(WORD_OR_GAP) ?? []) {
    if (IS_WORD.test(chunk)) {
      result += atSentenceStart ? chunk.charAt(0).toUpperCase() + chunk.slice(1) : chunk;
      atSentenceStart = false;
    } else {
      result += chunk;
      if (SENTENCE_END.test(chunk)) atSentenceStart = true;
    }
  }
  return result;
}
